<template>
	<div>
		<!-- Meta tags -->
		<template v-if="jobItem">
			<Title v-if="jobItem?.company?.name">{{ jobItem.name[locale] }} @ {{ jobItem.company.name }}</Title>
			<Title v-else>{{ jobItem.name[locale] }}</Title>
			<Meta
				name="description"
				:content="
					jobItem?.company?.boxes?.mission?.data?.mission
						? removeHtmlTagFromString(jobItem.company.boxes.mission.data.mission[locale])
						: jobItem.company?.name || ''
				"
			/>
			<Link rel="canonical" :href="'https://' + globalConfig.host + $route.fullPath" />

			<Meta
				v-if="jobItem?.company?.name"
				property="og:title"
				:content="jobItem.name[locale] + ' @ ' + jobItem.company.name"
			/>
			<Meta v-else property="og:title" :content="jobItem.name[locale]" />
			<Meta
				property="og:description"
				:content="
					jobItem?.company?.boxes?.mission?.data?.mission
						? removeHtmlTagFromString(jobItem.company.boxes.mission.data.mission[locale])
						: jobItem.company?.name || ''
				"
			/>
			<Meta property="og:url" :content="'https://' + globalConfig.host + $route.fullPath" />
			<Meta property="og:type" content="article" />
			<Meta
				property="og:locale"
				:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
			/>

			<Meta
				v-if="isRegion('hu') && jobItem.social_images && jobItem.social_images['fb-og']"
				property="og:image"
				:content="`${apiImageUrl}/social_images/${jobItem.social_images['fb-og']}`"
			/>
			<Meta
				v-else-if="jobItem.social_thumbnail && jobItem.social_thumbnail.image"
				property="og:image"
				:content="`https://res.cloudinary.com/djbs/image/upload/c_fit,h_2000,w_2000/${jobItem.social_thumbnail.image.cloudinary}.jpg`"
			/>
			<Meta
				v-else-if="jobItem?.company?.boxes?.team_photo?.data?.length"
				property="og:image"
				:content="`https://res.cloudinary.com/djbs/image/upload/c_fit,h_2000,w_2000/${jobItem.company.boxes.team_photo.data[0].cloudinary}.jpg`"
			/>
			<Meta
				v-else
				property="og:image"
				content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
			/>

			<Meta
				v-if="isRegion('hu') && jobItem.social_images && jobItem.social_images['fb-og']"
				property="og:image:secure_url"
				:content="`${apiImageUrl}/social_images/${jobItem.social_images['fb-og']}`"
			/>
			<Meta
				v-else-if="jobItem.social_thumbnail && jobItem.social_thumbnail.image"
				property="og:image:secure_url"
				:content="`https://res.cloudinary.com/djbs/image/upload/c_fit,h_2000,w_2000/${jobItem.social_thumbnail.image.cloudinary}.jpg`"
			/>
			<Meta
				v-else-if="jobItem?.company?.boxes?.team_photo?.data?.length"
				property="og:image:secure_url"
				:content="`https://res.cloudinary.com/djbs/image/upload/c_fit,h_2000,w_2000/${jobItem.company.boxes.team_photo.data[0].cloudinary}.jpg`"
			/>
			<Meta
				v-else
				property="og:image:secure_url"
				content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
			/>

			<Meta property="og:image:type" content="image/jpeg" />
			<Meta
				v-if="jobItem?.company?.name"
				property="og:image:alt"
				:content="jobItem.name[locale] + ' @ ' + jobItem.company.name"
			/>
			<Meta v-else property="og:image:alt" :content="jobItem.name[locale]" />
			<Meta property="og:image:width" content="1200" />
			<Meta property="og:image:height" content="630" />
		</template>
		<section class="relative flex flex-col bg-[#F9FBFE]">
			<JobProfileStickyBar
				v-if="jobItem?.boxes?.job_salary?.data"
				v-show="showSticky"
				:job-data="jobItem"
				:saved-job="savedJob"
				:already-applied="alreadyApplied"
				@on-apply="openApplication"
				@on-saved-change="handleSavedJob"
			/>

			<div
				v-if="jobItem?.company?.boxes?.team_photo?.data?.length"
				class="flex items-center max-w-[1480px] md:max-w-[calc(100%_-_80px)] lg:max-w-[1480px] mx-auto w-full h-auto md:min-h-[435px] min-h-[150px] bg-dj-gray md:rounded-bl-[40px] md:rounded-br-[40px] overflow-hidden"
			>
				<BaseCloudImage
					v-if="jobItem?.company?.boxes?.team_photo?.data?.length"
					custom-class="w-full h-auto hidden"
					:public-id="jobItem.company.boxes.team_photo.data[0].cloudinary"
					:transformation="[{ width: 1480, crop: 'scale' }]"
					postfix=".jpg"
					:alt="jobItem.company.name"
					@getUrl="imageUrl = $event"
					@getSize="imageSize = $event"
				/>
				<ImageSlider
					v-if="jobItem?.company?.coverImages?.length > 0"
					autoSlides
					:images="jobItem.company.coverImages"
				/>
			</div>
			<div class="container relative z-10 flex flex-col gap-y-6 md:gap-y-10 my-8">
				<JobProfileHeader
					v-if="jobItem?.boxes?.job_salary?.data"
					class="relative z-20"
					:job-data="jobItem"
					:saved-job="savedJob"
					:already-applied="alreadyApplied"
					:show-company="showCompany"
					@apply="openApplication"
					@on-saved-change="handleSavedJob"
				/>
				<BannerContainer
					banner-type="frontend-job-profile-banner"
					class="banner-section relative z-10"
					v-slot="{ content }"
				>
					<section
						class="relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto"
						v-html="content"
					></section>
				</BannerContainer>
				<!-- Job boxes -->
				<JobProfileBoxes class="relative z-10" v-if="jobItem?.boxes?.job_salary?.data" :job-data="jobItem" />

				<!-- Company boxes -->
				<div class="flex flex-col gap-y-6" v-if="showCompany">
					<div
						class="djcontainer w-full"
						v-if="jobItem?.company?.boxes && Object.keys(jobItem.company.boxes).length > 0"
					>
						<BaseHeading
							type="h5"
							class="text-left mt-4 relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
						>
							{{ $t("company.about") }}
						</BaseHeading>
					</div>

					<div v-if="jobItem?.company?.boxes?.mission?.data?.mission?.[locale]">
						<DjBoxRte
							class="hidden md:block"
							:title="$t('company.mission')"
							:copy="jobItem.company.boxes.mission.data.mission[locale]"
							without-margin
						></DjBoxRte>
					</div>

					<CompanyProfileBoxes
						v-if="jobItem?.company?.boxes?.team_photo?.data?.length"
						:company-data="jobItem.company"
						:langs="langs"
						:locale="locale"
						:hidden-title="true"
					/>
				</div>
			</div>
			<SectionsBlog
				v-if="jobItem?.company?.blog_posts?.length > 0"
				:blog-data="jobItem.company.blog_posts"
				:blog-title="$t('blogposts')"
			/>

			<JobsPageCarousel
				v-if="jobItem?.company && carouselData.length > 0"
				id="company-profile-carousel"
				:title="$t('company.openJobs', { company: jobItem.company.name })"
				:raw-jobs-data="carouselData"
				hide-all-jobs
				carouselType=""
			/>
			<template v-if="isAuth">
				<JobsPageCarousel
					v-if="jobItem"
					:title="$t('jobs.recommended')"
					:carousel-data-url="recommendedCarouselUrl"
					:data-url="recommendedDataUrl"
					hasGradientBg
					route-param="allRecommendedJobs"
					@data-fetched="processApiData"
					:filterJobId="jobItem.id"
					sortByGold
					carouselType="offered-jobs"
				/>
			</template>
			<template v-else>
				<JobsPageCarousel
					v-if="jobItem"
					:title="$t('jobs.highlighted')"
					highlighted-gold
					hasGradientBg
					route-param="highlightedJobs"
					@data-fetched="processApiData"
					carouselType="higlighted-company-jobs"
				/>
			</template>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { until } from "@vueuse/core";
import { isRegion } from "@@/composables/helpers";
import { useJob } from "@@/composables/useJob";
import { useErrorHandler } from "@@/composables/useErrorHandler";
import { useJobSchema } from "@@/composables/microdata/useJobSchema";
import { ApplicationsRepository } from "~/repositories/ApplicationsRepository";

export default defineComponent({
	name: "JobProfilePage",
	async setup() {
		const route = useRoute();
		const router = useRouter();
		const { locale } = useI18n();
		const gtm = useGTM();

		const isAuth = useState("isAuth");

		// For OG meta tags
		const imageUrl = ref("");
		const imageSize = ref({ width: 0, height: 0 });

		const showSticky = useState("showSticky", () => false);

		const savedJob = ref(false);

		const langs = useLangs.jobProfilePage;

		const recommendedCarouselUrl = ref("jobs?");
		const recommendedDataUrl = ref(globalConfig.API);

		const apiImageUrl = ref(globalConfig.API.replace(/\/api\/v\d+(\.\d+)?/, ""));

		const showCompany = computed(() => {
			return jobItem?.value?.company_id !== null && (jobItem?.value?.company?.status ?? true);
		});

		watch(
			() => (isAuth.value && jobItem.value ? useAuth.isSavedJob(jobItem.value.id) : false),
			(isSaved) => {
				savedJob.value = isSaved;
			},
			{ immediate: true }
		);

		onMounted(() => {
			window.scrollTo(0, 0);
			window.addEventListener("scroll", handleScroll);

			if (route.query.m != undefined && jobItem.value) {
				if (jobItem.value.application_url) {
					setTimeout(() => {
						window.open(jobItem.value.application_url, "_blank", "");
						new ApplicationsRepository()
							.storeAts({
								companySlug: jobItem.value.company?.slug,
								jobSlug: jobItem.value.slug,
								jobId: jobItem.value.id,
								xsrf: useAuth.getXsrfToken(),
								token: useAuth.getToken() || "",
							})
							.then(() => {
								useAuth.updateUser();
							})
							.catch((error) => {
								console.error("ats-application", error);
							});
					}, 1500);
				} else {
					openApplication();
				}
			}

			// GTM tracking
			if (jobItem.value?.id) {
				gtm.trackEvent({ event: "job-view", job_id: jobItem.value.id });
			}
		});

		onUnmounted(() => {
			// Remove scroll event listener
			window.removeEventListener("scroll", handleScroll, false);
		});

		// useJob
		const slugParam = route.params.slug;
		const jobSlug = Array.isArray(slugParam) ? slugParam[0] : slugParam;

		const { jobItem, loading, error } = useJob(jobSlug, locale.value);
		const alreadyApplied = computed(() => {
			const jobId = jobItem.value?.id;
			if (!isAuth.value || jobId == null) return false;
			if (useAuth.isApplied(jobId)) return true;

			const applications = useAuth.getUser()?.applications;
			return Array.isArray(applications) && applications.some((application) => application.id == jobId);
		});
		const { handleApiError } = useErrorHandler();

		// Várjuk meg, amíg a betöltés befejeződik
		await until(loading).toBe(false);

		// Create JSON-LD schema only if the job has not expired
		if (jobItem.value && !jobItem.value.expiredJob) {
			useJobSchema(jobItem, locale); // Assuming this is safe to call dynamically, otherwise use useHead with getters.
		}

		if (error.value) {
			handleApiError(error.value, "useJob");
			return;
		}

		if (jobItem.value) {
			await jobItem.value.loadCompany();
		} else {
			handleApiError(new Error("Job not found"), "jobSlug");
			console.error("useJob: jobItem.value is null after loading completed");
		}

		function handleScroll() {
			const scrollPosition = window.scrollY;
			const anchorTop = document.getElementById("job-anchor-top");

			if (anchorTop != null && scrollPosition > anchorTop.offsetTop) {
				showSticky.value = true;
			} else {
				showSticky.value = false;
			}
		}

		// Company's jobs without current job
		const carouselData = computed(() => {
			if (!jobItem?.value?.company?.jobs) {
				return [];
			}
			return jobItem.value.company.jobs.filter((job) => job.id != jobItem.value.id);
		});

		function processApiData(event) {
			// apiUrlFromCarousel.value = event.url
			// searchingFromCarousel.value = true
			const router = useRouter();
			const query = {};
			query[event.param] = "true";
			router.push({
				path: `/${locale.value}/jobs`,
				query: query,
			});
		}

		function handleSavedJob(event) {
			event ? (savedJob.value = true) : (savedJob.value = false);
		}

		function openApplication() {
			if (!jobItem.value || alreadyApplied.value) return;

			if (jobItem.value.application_url) {
				gtm.trackEvent({ event: "b2c-application-button-ats" });
				if (!isAuth.value) {
					const returnPath = `${route.fullPath.split("?")[0]}?m=1`;
					router.push(`/${locale.value}/login?redirect_url=${encodeURIComponent(returnPath)}`);
					return;
				}

				window.open(jobItem.value.application_url, "_blank", "");
				new ApplicationsRepository()
					.storeAts({
						companySlug: jobItem.value.company?.slug,
						jobSlug: jobItem.value.slug,
						jobId: jobItem.value.id,
						xsrf: useAuth.getXsrfToken(),
						token: useAuth.getToken() || "",
					})
					.then(() => {
						useAuth.updateUser();
					})
					.catch((error) => {
						console.error("ats-application", error);
					});
				return;
			}

			const company = Array.isArray(route.params.company_slug)
				? route.params.company_slug[0]
				: route.params.company_slug;
			const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;
			const applyPath = `/${locale.value}/apply/${company}/${slug}`;

			if (!isAuth.value) {
				router.push(`/${locale.value}/login?redirect_url=${encodeURIComponent(applyPath)}`);
				return;
			}

			router.push(applyPath);
		}

		return {
			locale,
			isAuth,
			showSticky,
			langs,
			carouselData,
			recommendedCarouselUrl,
			recommendedDataUrl,
			processApiData,
			alreadyApplied,
			openApplication,
			savedJob,
			handleSavedJob,
			removeHtmlTagFromString,
			imageUrl,
			imageSize,
			apiImageUrl,
			isRegion,
			showCompany,
			jobItem,
		};
	},
});
</script>

<style lang="scss">
.content-rte a {
	text-decoration: underline;
	color: #e1415a;
}
.dj-box-job-about p {
	margin: revert;
}
</style>
